import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ReactFlowProvider, MarkerType } from "reactflow";
import { useParams, useNavigate, unstable_useBlocker as useBlocker, useBeforeUnload } from "react-router-dom";

import { makeStyles } from "tss-react/mui";
import Backdrop from "@mui/material/Backdrop";
import Grid from "@mui/material/Grid";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from "@mui/icons-material/Settings";

import { ReflexContainer, ReflexElement, ReflexSplitter } from "react-reflex";
import "react-reflex/styles.css";

import { handlePipelineList } from "../../../../store/actions/datapipeline";
import { showToast } from "../../../../store/actions";

import { setPrevState } from "../../../../util/common";

import CommonLayout from "../../../Common/Layout";
import { ButtonUI, KMKBackButton } from "../../../Common";
import EditPanel from "./EditPanel";
import Sidebar from "./Sidebar";
import Canvas from "./Canvas";

import { MESSAGES } from "../utils/JobData";
import styles from "../index.module.css";
import "reactflow/dist/style.css";
import "./overview.css";

import DataPipelineService from "../../../../service/dataPipelineService";
import { navScheduledJobs } from "../../../kmk/AppData";
import SettingsPanel from "./SettingsPanel";
import HelpContextIcon from "../../../Common/HelpPanel/HelpContextIcon";
import PipelineHistory from "../JobHistory/PipelineHistory";

const useStyles = makeStyles()((theme) => ({
  TopPanelContainer: {
    height: "100%",
    width: "100%",
  },
  CanvasContainer: {
    height: "100%",
    maxWidth: "100%",
    overflow: "hidden",
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    marginRight: 20,
    paddingBottom: 0,
    paddingRight: "5px",
    display: "inline-block",
    verticalAlign: "middle",
  },
}));

const getEdgeParams = (jobType, sourceHandle) => {
  if (sourceHandle === "bottom")
    return {
      style: {
        stroke: "red",
      },
    };

  if (jobType === 8 && sourceHandle === "right_bottom")
    return {
      label: "No",
    };

  if (jobType === 8)
    return {
      label: "Yes",
    };

  return {};
};

const getMarkerParams = (sourceHandle) => {
  if (sourceHandle === "bottom")
    return {
      color: "red",
    };

  return {};
};

const Settings = ({
  entryJobs,
  pipelineName,
  isDefault,
  entryPoint,
  handleSettingsSave,
  successEnable,
  successEmails,
  errorEnable,
  errorEmails,
}) => {
  const [open, setOpen] = useState(false);

  const handleClose = useCallback(() => setOpen(false), []);

  const defaultValues = useMemo(
    () => ({ pipelineName, isDefault, entryPoint, entryJobs, successEnable, successEmails, errorEnable, errorEmails }),
    [pipelineName, isDefault, entryPoint, entryJobs, successEnable, successEmails, errorEnable, errorEmails]
  );

  const onSave = useCallback(
    (newValues) => {
      handleSettingsSave(newValues);
      handleClose();
    },
    [handleClose, handleSettingsSave]
  );

  return (
    <>
      <IconButton onClick={() => setOpen(true)} style={{ float: "right", color: "#3a3d4a" }}>
        <SettingsIcon />
      </IconButton>
      {open && <SettingsPanel defaultValues={defaultValues} handleClose={handleClose} onSave={onSave} />}
    </>
  );
};

const History = ({ PipelineName }) => {
  const { DataPipeline_id } = useParams();
  const [open, setOpen] = useState(false);

  const handleCancel = () => setOpen(false);

  return (
    <>
      <ButtonUI
        variant="contained"
        color="info"
        onClick={() => setOpen(true)}
        style={{ float: "right", marginTop: "4px" }}
      >
        View History
      </ButtonUI>
      {open && (
        <PipelineHistory DataPipeline_id={DataPipeline_id} PipelineName={PipelineName} handleCancel={handleCancel} />
      )}
    </>
  );
};

const DataPipeline = () => {
  const { Workspace_id, DataPipeline_id } = useParams();
  const { classes } = useStyles();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const workspace = useSelector((state) => state.workspace);
  const canvasRef = useRef(null);
  const editPanelRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [defaultViewport, setDefaultViewport] = useState(null);

  const { editJob } = useMemo(() => dataPipeline, [dataPipeline]);

  const [pipelineState, setPipelineState] = useState({
    open: false,
    pipelineName: "",
    loading: false,
    loadingForConfirm: false,
    dataPipelineNames: [],
    entryPoint: "",
    isDefault: 0,
    successEnable: false,
    successEmails: [],
    errorEnable: false,
    errorEmails: [],
  });

  const pipelineRef = useRef({});

  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPanel, setLoadingPanel] = useState(false);

  const entryJobs = useMemo(() => {
    const $nodes = canvasRef.current?.nodes || nodes;

    return $nodes.filter(({ type }) => type === "jobNode").map(({ id, data }) => ({ text: data.label, value: id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef.current?.nodes, nodes]);

  // Previous version of nodes and edges so that we can compare before leaving the page and restrict user if any changes
  const prevNodes = useRef([]);
  const prevEdges = useRef([]);

  const { Name: workspaceName } = useMemo(() => workspace.workspace, [workspace.workspace]);

  const handleSavePipeline = () => {
    if (editJob !== null) return dispatch(showToast({ message: MESSAGES.DataPipeline.EditPanelOpen, type: "error" }));

    if (!DataPipeline_id) canvasRef.current.addNewDataPipeline();
    else if (canvasRef.current) canvasRef.current.saveDataPipeline();
  };

  useEffect(() => {
    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const { data, success } = await pipeline.getDataPipelineList(Workspace_id);
      if (success && data?.dataPipelineList?.length) return data.dataPipelineList;
      return [];
    };

    const generateName = async () => {
      let dataPipelineNames = await getDataPipelineList();
      let counter = dataPipelineNames.length + 1;
      const generateNewPipelineName = () => `My Pipeline ${counter}`;
      let newPipelineName = generateNewPipelineName();
      const lowercaseDataPipelineNames = dataPipelineNames.map(({ Name }) => Name.toLowerCase());

      while (lowercaseDataPipelineNames.includes(newPipelineName.toLowerCase())) {
        counter += 1;
        newPipelineName = generateNewPipelineName();
      }
      setPrevState(setPipelineState, { pipelineName: newPipelineName });
    };

    const getDataPipeline = async () => {
      if (!DataPipeline_id) return await generateName();

      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipeline(Workspace_id, DataPipeline_id);
      if (!response.success) return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      const {
        nodes,
        edges,
        name,
        viewport,
        isDefault,
        entryPoint,
        successEnable = false,
        successEmails = [],
        errorEnable = false,
        errorEmails = [],
      } = response.data;

      setNodes(nodes);
      prevNodes.current = nodes.map(({ id, position, data }) => ({ id, position, label: data.label }));
      prevEdges.current = edges.map(({ id, source, target }) => ({ id, source, target }));

      setEdges(
        edges.map((edge) => {
          const currentNodeType = nodes.find(({ id }) => id === edge.source)?.data?.type || 1;

          return {
            ...edge,
            ...getEdgeParams(currentNodeType, edge.sourceHandle),
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 30,
              height: 30,
              ...getMarkerParams(edge.sourceHandle),
            },
          };
        })
      );
      if (viewport) setDefaultViewport(viewport);

      const stateData = {
        pipelineName: name,
        isDefault: !!isDefault,
        entryPoint: entryPoint || "",
        successEnable,
        successEmails,
        errorEnable,
        errorEmails,
      };

      pipelineRef.current = { ...stateData };
      setPrevState(setPipelineState, stateData);
    };

    const fetchData = async () => {
      setLoading(true);
      await getDataPipeline();
      setLoading(false);
    };
    fetchData();
  }, [DataPipeline_id, Workspace_id, dispatch, navigate]);

  useEffect(() => {
    return () => {
      setNodes([]);
      setEdges([]);
      setPrevState(setPipelineState, { open: false });
      setLoading(false);
      dispatch(handlePipelineList(false));
    };
  }, [dispatch]);

  // If edit panel is open and made any changes or any changes on canvas then restric user to leave that page
  let blocker = useBlocker(() => {
    if ((editJob !== null && editPanelRef.current.restrictOrNot()) || canvasRef.current.restrictOrNot()) {
      return !window.confirm("You have unsaved changes. Are you sure you want to leave this page?");
    }
    return false;
  });

  useBeforeUnload((event) => {
    if ((editJob !== null && editPanelRef.current.restrictOrNot()) || canvasRef.current.restrictOrNot()) {
      event.preventDefault();
      return (event.returnValue = "");
    }
  });

  useEffect(() => {
    return () => {
      if (blocker.state === "blocked") blocker.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/my-workspaces", displayName: "Workspaces", active: false },
      { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
      { link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`, displayName: "Data Pipelines", active: false },
      { link: "#", displayName: `${DataPipeline_id ? "Edit" : "Add"} Data Pipeline`, active: true },
    ],
    [Workspace_id, workspaceName, DataPipeline_id]
  );

  const handleSettingsSave = useCallback(
    (newValues) => {
      setPipelineState((prev) => ({ ...prev, ...newValues }));
    },
    [setPipelineState]
  );

  return (
    <CommonLayout
      title={
        <>
          Data Pipeline
          <HelpContextIcon screen="Data Pipeline Canvas" />
        </>
      }
      loading={loading}
      breadcrumbArr={breadcrumbArr}
    >
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
      >
        <Backdrop sx={{ zIndex: 99999, background: "transparent" }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        {!loading && (
          <Box sx={{ mb: 4, mx: 0 }}>
            <Backdrop sx={{ zIndex: 99999, background: "transparent" }} open={loadingPanel}>
              <CircularProgress color="primary" />
            </Backdrop>
            <Box sx={{ display: "flex", alignItems: "center" }} my={2} mx={3}>
              <Grid container spacing={2}>
                <Grid
                  size={{
                    xs: 8,
                    md: 8
                  }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <KMKBackButton
                      handleClick={() => navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`)}
                    ></KMKBackButton>

                    <Typography className={classes.HeaderTitle}>{pipelineState.pipelineName}</Typography>

                    <ButtonUI variant="contained" color="success" onClick={handleSavePipeline}>
                      Save
                    </ButtonUI>
                  </Box>
                </Grid>
                <Grid
                  alignItems="end"
                  size={{
                    xs: 4,
                    md: 4
                  }}>
                  <Settings
                    entryJobs={entryJobs}
                    pipelineName={pipelineState.pipelineName}
                    isDefault={pipelineState.isDefault}
                    entryPoint={pipelineState.entryPoint}
                    handleSettingsSave={handleSettingsSave}
                    successEnable={pipelineState.successEnable || false}
                    successEmails={pipelineState.successEmails || []}
                    errorEnable={pipelineState.errorEnable || false}
                    errorEmails={pipelineState.errorEmails || []}
                  />
                  <History PipelineName={pipelineState.pipelineName} />
                </Grid>
              </Grid>
            </Box>
            <div style={{ width: "calc(100% - 50px)", height: "calc(100vh - 160px)", margin: "0 25px 25px 25px" }}>
              <Paper className={`${classes.TopPanelContainer} dndflow`}>
                <ReactFlowProvider>
                  <Sidebar />
                  <ReflexContainer
                    windowResizeAware
                    orientation="horizontal"
                    style={{ maxWidth: "calc(100% - 100px)" }}
                  >
                    <ReflexElement
                      minSize={100}
                      className={styles.resizeDiv}
                      style={{
                        margin: 0,
                        padding: 0,
                        overflow: "hidden",
                        maxWidth: "100%",
                      }}
                      propagateDimensionsRate={50}
                      propagateDimensions={true}
                    >
                      <Paper className={`${classes.CanvasContainer} dndflow`}>
                        <Canvas
                          defaultViewport={defaultViewport}
                          initialNodes={nodes}
                          initialEdges={edges}
                          ref={canvasRef}
                          pipelineName={pipelineState.pipelineName}
                          editPanelRef={editPanelRef}
                          prevNodes={prevNodes}
                          pipelineRef={pipelineRef}
                          prevEdges={prevEdges}
                          entryPoint={pipelineState.entryPoint}
                          isDefault={pipelineState.isDefault}
                          successEnable={pipelineState.successEnable || false}
                          successEmails={pipelineState.successEmails || []}
                          errorEnable={pipelineState.errorEnable || false}
                          errorEmails={pipelineState.errorEmails || []}
                        />
                      </Paper>
                    </ReflexElement>
                    {editJob !== null && <ReflexSplitter className={styles.resizeBorder} propagate={true} />}
                    {editJob !== null && (
                      <ReflexElement minSize={300} size={500} className={styles.resizeDiv}>
                        <EditPanel
                          loading={loadingPanel}
                          setLoading={setLoadingPanel}
                          canvasRef={canvasRef}
                          ref={editPanelRef}
                        />
                      </ReflexElement>
                    )}
                  </ReflexContainer>
                </ReactFlowProvider>
              </Paper>
            </div>
          </Box>
        )}
      </Box>
    </CommonLayout>
  );
};

export default DataPipeline;
